import { useRouter } from '@tanstack/react-router';
import { matchSorter } from 'match-sorter';
import { useCallback, useMemo, useState } from 'react';
import Link from 'src/components/atoms/Link';
import { buildAppHref } from 'src/lib/routing';
import { InventorySettingsTabs } from 'src/modules/settings/inventory/inventorySettings.constants';
import { appLinks } from 'src/routing/appLinks';
import formatAddress from 'src/utils/address/formatAddress';
import { AddressRole } from 'types/graphql';
import { AgAutocomplete, AgAutocompleteProps } from './AgAutocomplete';
import { AgText } from './AgText';

export interface AgAddressOption {
  __typename?: 'Place';
  id: number;
  description: string;
  fullAddress?: string;
  streetAddress1?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  addressCountry?: string;
  addressRoles?: AddressRole[];
  businessEntityId?: number;
}

export interface AgAddressAutocompleteProps
  extends Omit<
    AgAutocompleteProps<AgAddressOption>,
    'options' | 'getOptionLabel' | 'getOptionKey' | 'renderOption' | 'filterOptions' | 'onInputChange'
  > {
  /** The list of addresses to select from */
  addressBook: AgAddressOption[];
  /** Business entity ID for linking to address settings */
  businessEntityId?: number;
  /** Whether to hide the "Create new address" link */
  noLink?: boolean;
  /** Called with what the user types, for an `addressBook` searched on the server (TAS-3660 P7 BZ77). */
  onSearchChange?: (searchText: string) => void;
}

export function AgAddressAutocomplete({
  addressBook,
  businessEntityId,
  noLink,
  value,
  onChange,
  onSearchChange,
  ...props
}: AgAddressAutocompleteProps) {
  const router = useRouter();
  const [searchValue, setSearchValue] = useState('');
  const handleSearchChange = useCallback(
    (text: string) => {
      setSearchValue(text);
      onSearchChange?.(text);
    },
    [onSearchChange]
  );

  // Get the label for an address option
  const getOptionLabel = useCallback((option: AgAddressOption) => {
    const fallbackAddress = formatAddress(option as any) || option?.streetAddress1 || '--';
    return option?.description || fallbackAddress;
  }, []);

  // Create link for "Create new address"
  const createLink = useMemo(() => {
    if (noLink) return null;
    return buildAppHref(
      router,
      businessEntityId
        ? appLinks.settingsEntities({ id: businessEntityId, tab: 'locations' })
        : appLinks.settingsInventory({ tab: InventorySettingsTabs.WAREHOUSES })
    );
  }, [businessEntityId, noLink, router]);

  // Filter options using fuzzy matching on description and streetAddress1
  const filteredOptions = useMemo(() => {
    if (!searchValue) return addressBook;

    const terms = searchValue.split(' ');
    return terms.reduceRight(
      (opts, term) => matchSorter(opts, term, { keys: ['description', 'streetAddress1'] }),
      addressBook
    );
  }, [addressBook, searchValue]);

  // Custom render for options showing description + address details
  const renderOption = useCallback(
    (option: AgAddressOption, { isSelected }: { isSelected: boolean; isFocused: boolean }) => {
      const isDisplayingAddress = !option.description;
      const fallbackAddress = formatAddress(option as any) || option?.streetAddress1;

      return (
        <div className="flex flex-col gap-2 flex-1 min-w-0">
          <span className={`truncate ${isSelected ? 'font-medium' : ''}`}>{getOptionLabel(option)}</span>
          {!isDisplayingAddress && fallbackAddress && (
            <AgText size="14" color="secondary" className="truncate">
              {fallbackAddress}
            </AgText>
          )}
        </div>
      );
    },
    [getOptionLabel]
  );

  // Custom noOptionsText with link to create new address
  const noOptionsContent =
    !noLink && createLink ? <Link to={createLink} label="Create new address" /> : 'No addresses found';

  return (
    <AgAutocomplete<AgAddressOption>
      {...props}
      value={value}
      options={filteredOptions}
      getOptionLabel={getOptionLabel}
      getOptionKey={(option) => option.id}
      renderOption={renderOption}
      filterOptions={false}
      onInputChange={handleSearchChange}
      noOptionsText={noOptionsContent}
      onChange={onChange}
    />
  );
}

AgAddressAutocomplete.displayName = 'AgAddressAutocomplete';
